/**
 * Black Matte VTC - Partner Booking Detail
 * Pure view layer - Timeline from API /events
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { partnerService } from '../../services/partnerService';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Users,
  Phone,
  Mail,
  User,
  Building2,
  FileText,
  AlertTriangle,
  CheckCircle,
  Circle,
  CreditCard,
  Hash
} from 'lucide-react';

// Status badge - pure display
const BookingStatusBadge = ({ status }) => {
  const statusConfig = {
    confirmed: { label: 'Confirmed', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
    completed: { label: 'Completed', className: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    cancelled: { label: 'Cancelled', className: 'bg-red-500/10 text-red-400 border-red-500/20' },
    in_progress: { label: 'In Progress', className: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
    pending: { label: 'Pending', className: 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20' },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <Badge variant="outline" className={`text-sm px-3 py-1 ${config.className}`}>
      {config.label}
    </Badge>
  );
};

// Timeline event icon
const TimelineIcon = ({ status }) => {
  const icons = {
    created: <Circle className="h-4 w-4 text-neutral-400" />,
    confirmed: <CheckCircle className="h-4 w-4 text-emerald-500" />,
    assigned: <User className="h-4 w-4 text-blue-400" />,
    in_progress: <Clock className="h-4 w-4 text-amber-400" />,
    completed: <CheckCircle className="h-4 w-4 text-blue-500" />,
    cancelled: <AlertTriangle className="h-4 w-4 text-red-500" />,
  };
  return icons[status] || icons.created;
};

const PartnerBookingDetail = () => {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const data = await partnerService.getBooking(bookingId);
        setBooking(data);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId]);

  if (loading) {
    return <BookingDetailSkeleton />;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild className="text-neutral-400 hover:text-white">
          <Link to="/partner/bookings">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Bookings
          </Link>
        </Button>
        
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} variant="outline">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partner-booking-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" asChild className="text-neutral-400 hover:text-white">
            <Link to="/partner/bookings">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">
                {booking.booking_reference}
              </h1>
              <BookingStatusBadge status={booking.status} />
            </div>
            <p className="text-neutral-500">
              Created {new Date(booking.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Trip Details */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Trip Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Pickup</p>
                  <p className="text-white">{booking.pickup_address}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Destination</p>
                  <p className="text-white">{booking.destination_address}</p>
                </div>
              </div>

              <Separator className="bg-neutral-800" />

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> Date
                  </p>
                  <p className="text-white">{booking.date}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Time
                  </p>
                  <p className="text-white">{booking.time}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                    <Users className="h-3 w-3" /> Passengers
                  </p>
                  <p className="text-white">{booking.passengers || 1}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Guest Info */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5" />
                Guest Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Name</p>
                  <p className="text-white">{booking.guest_name || '—'}</p>
                </div>
                {booking.room_number && (
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> Room
                    </p>
                    <p className="text-white">{booking.room_number}</p>
                  </div>
                )}
                {booking.phone && (
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Phone className="h-3 w-3" /> Phone
                    </p>
                    <p className="text-white">{booking.phone}</p>
                  </div>
                )}
                {booking.email && (
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <Mail className="h-3 w-3" /> Email
                    </p>
                    <p className="text-white">{booking.email}</p>
                  </div>
                )}
              </div>
              
              {booking.notes && (
                <>
                  <Separator className="bg-neutral-800 my-4" />
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                      <FileText className="h-3 w-3" /> Notes
                    </p>
                    <p className="text-neutral-300">{booking.notes}</p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Status Timeline - FROM API EVENTS */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Status Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              {booking.status_timeline && booking.status_timeline.length > 0 ? (
                <div className="space-y-4" data-testid="booking-timeline">
                  {booking.status_timeline.map((event, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <TimelineIcon status={event.status} />
                        {index < booking.status_timeline.length - 1 && (
                          <div className="w-px h-full bg-neutral-700 mt-2" />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="text-sm font-medium text-white capitalize">
                          {event.status.replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {new Date(event.timestamp).toLocaleString()}
                        </p>
                        {event.source && (
                          <p className="text-xs text-neutral-600">
                            Source: {event.source}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-neutral-500 text-sm">No timeline events</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing - ALL from API */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Pricing
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-neutral-400">Estimated Fare</span>
                <span className="text-2xl font-bold text-white" data-testid="estimated-price">
                  €{booking.estimated_price?.toFixed(2) || '—'}
                </span>
              </div>

              {booking.fare_breakdown && (
                <>
                  <Separator className="bg-neutral-800" />
                  <div className="space-y-2 text-sm">
                    {booking.fare_breakdown.base_fare && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Base Fare</span>
                        <span className="text-neutral-300">€{booking.fare_breakdown.base_fare.toFixed(2)}</span>
                      </div>
                    )}
                    {booking.fare_breakdown.distance_fare && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Distance</span>
                        <span className="text-neutral-300">€{booking.fare_breakdown.distance_fare.toFixed(2)}</span>
                      </div>
                    )}
                    {booking.fare_breakdown.time_fare && (
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Time</span>
                        <span className="text-neutral-300">€{booking.fare_breakdown.time_fare.toFixed(2)}</span>
                      </div>
                    )}
                    {booking.fare_breakdown.discount && booking.fare_breakdown.discount > 0 && (
                      <div className="flex justify-between text-emerald-400">
                        <span>Discount</span>
                        <span>-€{booking.fare_breakdown.discount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <Separator className="bg-neutral-800" />

              <div className="flex justify-between items-center">
                <span className="text-neutral-400">Payment Status</span>
                <Badge 
                  variant="outline" 
                  className="bg-amber-500/10 text-amber-400 border-amber-500/20"
                >
                  {booking.payment_status || 'Invoiced'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Reference Info */}
          <Card className="bg-neutral-900 border-neutral-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Hash className="h-5 w-5" />
                References
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Booking ID</p>
                <p className="text-white text-sm font-mono">{booking.id}</p>
              </div>
              
              {booking.partner_reference && (
                <div>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Your Reference</p>
                  <p className="text-white">{booking.partner_reference}</p>
                </div>
              )}

              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Partner</p>
                <p className="text-white">{booking.partner_name}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Loading skeleton
const BookingDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center gap-4">
      <Skeleton className="h-10 w-24 bg-neutral-800" />
      <div>
        <Skeleton className="h-8 w-48 bg-neutral-800" />
        <Skeleton className="h-4 w-32 bg-neutral-800 mt-2" />
      </div>
    </div>
    
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Skeleton className="h-48 w-full bg-neutral-800" />
        <Skeleton className="h-32 w-full bg-neutral-800" />
        <Skeleton className="h-64 w-full bg-neutral-800" />
      </div>
      <div className="space-y-6">
        <Skeleton className="h-48 w-full bg-neutral-800" />
        <Skeleton className="h-32 w-full bg-neutral-800" />
      </div>
    </div>
  </div>
);

export default PartnerBookingDetail;
